import { useEffect, useState } from "react";
import { Camera, Loader2, Mail, Megaphone, MessageCircle, Rocket, Target, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const ICONS = { target: Target, camera: Camera, megaphone: Megaphone, message: MessageCircle, mail: Mail, rocket: Rocket };

export const ReAiTeamTab = () => {
  const [agents, setAgents] = useState([]);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/admin/realestate/agency/agents").then((r) => setAgents(r.data.agents || [])).catch(() => {}); }, []);

  const run = async () => {
    if (!active || !input.trim()) return toast.error("Describe the task first");
    setRunning(true); setResult(null);
    try { const r = await api.post("/admin/realestate/agency/run", { agent_id: active.id, input: input.trim() }); setResult(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setRunning(false); }
  };

  return (
    <div data-testid="re-ai-team-tab" className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">AI Team</h2>
        <p className="mt-1 text-xs text-[#91837C]">A roster of specialist marketers for Docstate — captions, ad copy, follow-ups, outreach, lead finding and growth plans.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {agents.map((a) => {
          const Icon = ICONS[a.emoji] || Users;
          const sel = active?.id === a.id;
          return (
            <button key={a.id} data-testid={`re-team-agent-${a.id}`} onClick={() => { setActive(a); setInput(""); setResult(null); }}
              className={`glass rounded-2xl p-5 text-left transition-all hover:-translate-y-0.5 ${sel ? "ring-2 ring-[#C9A24B]" : ""}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F6F0E8] text-[#B38B2D]"><Icon className="h-5 w-5" /></div>
              <p className="mt-3 text-sm font-semibold text-[#1A1615]">{a.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-[#91837C]">{a.tagline}</p>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="glass rounded-2xl p-6" data-testid="re-team-runner">
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">{active.name} — {active.input_label}</label>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input data-testid="re-team-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder={active.input_placeholder}
              className="flex-1 rounded-xl border border-[#E6DCD2] bg-white px-4 py-3 text-sm outline-none focus:border-[#C9A24B]" />
            <button data-testid="re-team-run" onClick={run} disabled={running}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#B38B2D] px-5 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Run
            </button>
          </div>

          {result && (
            <div className="mt-5" data-testid="re-team-result">
              {result.leads ? (
                <div className="space-y-2">
                  <p className="text-xs text-[#6B5E57]">{result.output}</p>
                  {result.leads.map((l) => (
                    <div key={l.id} className="rounded-lg border border-[#EFE7DD] px-4 py-2 text-sm">
                      <span className="font-medium text-[#1A1615]">{l.name}</span>
                      {l.email && <span className="ml-2 text-[#B38B2D]">{l.email}</span>}
                      <a href={l.website} target="_blank" rel="noreferrer" className="ml-2 text-xs text-[#91837C] hover:text-[#B38B2D]">{l.website}</a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  <button onClick={() => { navigator.clipboard.writeText(result.output || ""); toast.success("Copied"); }} className="absolute right-0 top-0 inline-flex items-center gap-1 rounded-full border border-[#E6DCD2] px-3 py-1 text-xs text-[#6B5E57] hover:bg-[#F6F0E8]"><Copy className="h-3 w-3" /> Copy</button>
                  <pre className="whitespace-pre-wrap rounded-xl bg-[#FBF7F1] p-4 pr-20 text-sm leading-relaxed text-[#2A2320]">{result.output}</pre>
                  {result.provider && <p className="mt-2 text-[10px] uppercase tracking-wide text-[#B5A79E]">via {result.provider}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
